import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2Icon, SaveIcon, BellIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { thingsboardService } from '../services/thingsboard';
import { useTranslation } from 'react-i18next';

interface AlarmSoundSettingsProps {
  currentAssetId: string | null;
  onClose: () => void;
}

const DEFAULT_ALARM_SOUNDS = [
  { label: '짧고 강한 경고음 (비프)', value: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3' },
  { label: '에러 경고음 (버저)', value: 'https://assets.mixkit.co/active_storage/sfx/995/995-preview.mp3' },
  { label: '긴급 사이렌 (소방차)', value: 'https://assets.mixkit.co/active_storage/sfx/1003/1003-preview.mp3' },
  { label: '아날로그 알람 (시계)', value: 'https://assets.mixkit.co/active_storage/sfx/2870/2870-preview.mp3' },
];

export function AlarmSoundSettings({ currentAssetId, onClose }: AlarmSoundSettingsProps) {
  const { t } = useTranslation();
  const [alarmSounds, setAlarmSounds] = useState<{label: string, value: string}[]>([]);
  const [selectedSound, setSelectedSound] = useState<string>('');
  const [enabled, setEnabled] = useState<boolean>(true);
  const [amplitude, setAmplitude] = useState<number>(0.5);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const playingAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    async function loadSettings() {
      if (!currentAssetId) return;
      setLoading(true);
      try {
        const attrs = await thingsboardService.getEntityAttributes('ASSET', currentAssetId, 'SERVER_SCOPE', [
          'alarm_sounds', 
          'selected_sound', 
          'alarm_sound_enabled', 
          'sound_amplitude'
        ]);
        
        let availableSounds: {label: string, value: string}[] = [];
        
        attrs.forEach((attr: any) => {
          if (attr.key === 'alarm_sounds') {
            try {
              const parsed = typeof attr.value === 'string' ? JSON.parse(attr.value) : attr.value;
              if (Array.isArray(parsed)) {
                availableSounds = parsed.map(s => typeof s === 'string' ? { label: s.split('/').pop() || s, value: s } : {label: s.label || s.value, value: s.value});
              }
            } catch (e) {
              console.warn('Failed to parse alarm_sounds', e);
            }
          } else if (attr.key === 'selected_sound') {
            setSelectedSound(attr.value || '');
          } else if (attr.key === 'alarm_sound_enabled') {
            setEnabled(String(attr.value).toLowerCase() === 'true');
          } else if (attr.key === 'sound_amplitude') {
            setAmplitude(Number(attr.value) || 0.5);
          }
        });
        
        if (availableSounds.length === 0 || availableSounds[0].value.includes('.ogg')) {
          availableSounds = DEFAULT_ALARM_SOUNDS;
        }

        setAlarmSounds(availableSounds);
        let finalSound = selectedSound;
        if (!finalSound || finalSound.includes('.ogg')) {
          finalSound = availableSounds[0].value;
          setSelectedSound(finalSound);
        } else if (availableSounds.length > 0 && !finalSound) {
          finalSound = availableSounds[0].value;
          setSelectedSound(finalSound);
        }
      } catch (err) {
        console.error('Failed to load alarm settings', err);
      } finally {
        setLoading(false);
      }
    }
    loadSettings();
  }, [currentAssetId]);

  const handleSave = async () => {
    if (!currentAssetId) return;
    setSaving(true);
    try {
      await thingsboardService.saveEntityAttributes('ASSET', currentAssetId, 'SERVER_SCOPE', {
        selected_sound: selectedSound,
        alarm_sound_enabled: enabled,
        sound_amplitude: amplitude
      });
      onClose();
    } catch (err) {
      console.error('Failed to save alarm settings', err);
    } finally {
      setSaving(false);
    }
  };

  const playTestSound = () => {
    if (!selectedSound) return;
    
    // Stop currently playing sound to prevent overlaps
    if (playingAudioRef.current) {
      playingAudioRef.current.pause();
      playingAudioRef.current.currentTime = 0;
    }

    const audio = new Audio(selectedSound);
    audio.volume = amplitude;
    playingAudioRef.current = audio;

    audio.play().catch(e => console.error('Failed to play test sound', e));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-2xl backdrop-saturate-0" />
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-[90%] max-w-sm"
      >
        <div className="glass-panel rounded-2xl p-6 shadow-2xl border-t border-white/20">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-lg bg-red-500/15 text-red-400">
              <BellIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-base font-bold text-white">{t('settings.alarm_sound_settings')}</div>
              <div className="text-[0.65rem] text-slate-400">{t('settings.alarm_sound_desc')}</div>
            </div>
          </div>

          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500">{t('settings.loading')}</div>
          ) : (
            <div className="space-y-5">
              {/* Select Sound */}
              <div>
                <label className="block text-[0.7rem] font-bold text-slate-400 uppercase tracking-wider mb-2">{t('settings.select_alarm_sound')}</label>
                <div className="flex gap-2 relative" ref={selectRef}>
                  <div className="flex-1 relative">
                    <button
                      type="button"
                      onClick={() => setIsSelectOpen(!isSelectOpen)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 hover:border-red-400/50 text-sm text-white transition-all focus:outline-none focus:ring-1 focus:ring-red-400/50"
                    >
                      <span className="truncate font-medium">
                        {alarmSounds.find((s) => s.value === selectedSound)?.label || t('settings.select_sound_placeholder')}
                      </span>
                      <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isSelectOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {isSelectOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          transition={{ duration: 0.15 }}
                          className="absolute z-50 w-full mt-1.5 py-1.5 rounded-xl bg-[#1e293b] border border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.5)] overflow-hidden"
                        >
                          {alarmSounds.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">{t('settings.no_sounds_registered')}</div>}
                          {alarmSounds.map((snd, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setSelectedSound(snd.value);
                                setIsSelectOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors ${
                                selectedSound === snd.value
                                  ? 'bg-red-500/15 text-red-400 font-bold'
                                  : 'text-slate-300 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              <span className="truncate">{snd.label}</span>
                              {selectedSound === snd.value && <CheckIcon className="w-4 h-4 flex-shrink-0 ml-2" />}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <button onClick={playTestSound} disabled={!selectedSound} className="flex-shrink-0 p-2.5 bg-white/5 hover:bg-white/10 border border-transparent hover:border-white/10 rounded-lg text-slate-300 transition-all tooltip" title="미리듣기">
                    <Volume2Icon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Volume Slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-[0.7rem] font-bold text-slate-400 uppercase tracking-wider">{t('settings.volume')}</label>
                  <span className="text-[0.7rem] text-slate-300 font-roboto">{Math.round(amplitude * 100)}%</span>
                </div>
                <input
                  title="볼륨 조절"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={amplitude}
                  onChange={(e) => setAmplitude(Number(e.target.value))}
                  className="w-full accent-red-400"
                />
              </div>

              {/* Toggle Switch */}
              <div className="flex items-center justify-between bg-white/5 p-3 rounded-lg border border-white/5">
                <span className="text-sm font-medium text-slate-200">{t('settings.turn_on_alarm_sound')}</span>
                <button
                  title="경고음 켜기 토글"
                  type="button"
                  onClick={() => setEnabled(!enabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${enabled ? 'bg-red-500' : 'bg-white/20'}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

            </div>
          )}

          <div className="flex gap-3 justify-end mt-6">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-sm transition-all"
            >
              {t('settings.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="flex items-center gap-1.5 px-6 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm font-medium transition-all"
            >
              <SaveIcon className="w-4 h-4" />
              {saving ? t('settings.saving') : t('settings.save')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
